// État du service, réservé aux administrateurs. Lit /api/health/details.
// Sert à répondre à « est-ce que ça tourne ? » sans ouvrir un SSH, et
// à voir NOIR SUR BLANC ce qui est actif et ce qui ne l'est pas.
//
// Un mode dégradé annoncé vaut mieux qu'une fonction qu'on croit
// présente : les agents IA sans clé, l'envoi de mail sans SMTP.

import { useCallback } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Badge from '../../ui/Badge.jsx';
import Button from '../../ui/Button.jsx';
import { ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { formatEuro } from '../../lib/format.js';

const ORIGINES = [
  ['production', 'Chiffrage'],
  ['surveillance', 'Surveillance'],
  ['assistant', 'Assistant'],
  ['banc', 'Bancs (développement)'],
];

/** Le coût IA par mois et par ORIGINE : l'assistant se lit à part (14 octobre 2026). */
function CoutParOrigine({ lignes = [] }) {
  const mois = [...new Set(lignes.map((l) => l.mois))].sort().reverse();
  if (!mois.length) return <p className="field__hint">Aucun appel IA sur la période.</p>;
  const cout = (m, o) => lignes.find((l) => l.mois === m && l.origine === o)?.cost_eur ?? null;
  return (
    <table className="table">
      <thead>
        <tr>
          <th scope="col">Mois</th>
          {ORIGINES.map(([cle, libelle]) => <th key={cle} scope="col" className="num">{libelle}</th>)}
        </tr>
      </thead>
      <tbody>
        {mois.map((m) => (
          <tr key={m}>
            <th scope="row">{m}</th>
            {ORIGINES.map(([cle]) => <td key={cle} className="num">{formatEuro(cout(m, cle))}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Ligne({ label, children }) {
  return (
    <div className="price-summary__row">
      <span className="price-summary__label">{label}</span>
      <span>{children}</span>
    </div>
  );
}

export function EtatPage() {
  const load = useCallback(() => api.get('/health/details'), []);
  const { data, error, loading, reload } = useResource(load);

  if (loading) return <SkeletonRows rows={5} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const uptimeH = Math.floor(data.uptime_s / 3600);
  const uptimeM = Math.floor((data.uptime_s % 3600) / 60);

  return (
    <>
      <Card>
        <CardHead title="Service">
          <Button size="sm" variant="secondary" onClick={reload}>
            Actualiser
          </Button>
        </CardHead>
        <CardBody>
          <div className="price-summary">
            <Ligne label="Environnement">
              <Badge tone={data.env === 'production' ? 'ok' : 'warn'}>{data.env}</Badge>
            </Ligne>
            <Ligne label="En ligne depuis">
              <span className="num">
                {uptimeH} h {String(uptimeM).padStart(2, '0')}
              </span>
            </Ligne>
            <Ligne label="Node">
              <span className="num">{data.node}</span>
            </Ligne>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Base de données" />
        <CardBody>
          <div className="price-summary">
            <Ligne label="Connexion">
              <Badge tone={data.db.ok ? 'ok' : 'danger'}>{data.db.ok ? 'Joignable' : 'Injoignable'}</Badge>
            </Ligne>
            <Ligne label="Latence">
              <span className="num">{data.db.latency_ms} ms</span>
            </Ligne>
            <Ligne label="Dernière migration">
              <span className="num">{data.db.last_migration ?? '—'}</span>
            </Ligne>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Coût IA par mois" />
        <CardBody>
          <CoutParOrigine lignes={data.ai_cost?.by_origin_month ?? []} />
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Fonctions" />
        <CardBody>
          <div className="price-summary">
            <Ligne label="Agents IA (lecture de plan, proposition de temps)">
              <Badge tone={data.features.ai ? 'ok' : 'warn'}>
                {data.features.ai ? 'Actifs' : 'Désactivés — clé API absente'}
              </Badge>
            </Ligne>
            <Ligne label="Envoi de devis par e-mail">
              <Badge tone={data.features.mail ? 'ok' : 'warn'}>
                {data.features.mail ? 'Actif' : 'Désactivé — SMTP non configuré'}
              </Badge>
            </Ligne>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

export default EtatPage;
